import React, { useState } from 'react';
import Skeleton from '@material-ui/lab/Skeleton';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { useUserDashboard } from './useUserDashboard';
import { ActiveUsersCard } from './components/ActiveUsersCard';
import { NewUsersCard } from './components/NewUsersCard';
import { RolesChart } from './components/RolesChart';
import { EngagementCard } from './components/EngagementCard';
import { DepartmentsTable } from './components/DepartmentsTable';
import { UserDashboardTable } from './components/UserDashboardTable';

export const UserDashboardPage: React.FC = () => {
  useDocumentTitle('User Dashboard');

  const { data, loading, error, refetch } = useUserDashboard();
  const [departmentFilter, setDepartmentFilter] = useState<number | undefined>(undefined);

  const handleDepartmentClick = (departmentId: number) => {
    setDepartmentFilter(departmentId);
    const table = document.getElementById('user-dashboard-table');
    if (table) {
      table.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (error) {
    return (
      <div style={{ padding: '40px 32px' }}>
        <div style={{ maxWidth: 480, margin: '80px auto', textAlign: 'center' }}>
          <div style={{
            width: 40, height: 40, borderRadius: '50%', backgroundColor: '#fef2f2',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16,
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <p style={{ fontSize: 14, fontWeight: 500, color: '#111827', marginBottom: 4 }}>Unable to load dashboard</p>
          <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 20 }}>{error}</p>
          <button
            onClick={refetch}
            style={{
              padding: '8px 16px', backgroundColor: '#111827', color: '#fff',
              fontSize: 13, fontWeight: 500, borderRadius: 8, border: 'none', cursor: 'pointer',
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#fff', height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ padding: '32px 32px 0', backgroundColor: '#fff' }}>
        <h1 style={{ fontSize: 18, fontWeight: 600, color: '#111827', margin: 0 }}>User Dashboard</h1>
        <p style={{ fontSize: 13, color: '#6B7280', margin: '4px 0 0' }}>
          User activity, engagement, and department overview
        </p>
      </div>

      {/* Scrollable dashboard content surface */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '32px 32px 48px', backgroundColor: '#fff' }}>
        {loading ? (
          <DashboardSkeleton />
        ) : data ? (
          <div>
            {/* Summary Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, marginBottom: 32 }}>
              <ActiveUsersCard data={data.active_users} />
              <NewUsersCard data={data.new_users} />
            </div>

            {/* Analytics Row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, marginBottom: 32 }}>
              <RolesChart data={data.roles} />
              <EngagementCard data={data.engagement} />
            </div>

            {/* Departments */}
            {data.departments.length > 0 && (
              <div style={{ marginBottom: 32 }}>
                <DepartmentsTable
                  departments={data.departments}
                  onDepartmentClick={handleDepartmentClick}
                />
              </div>
            )}

            {/* User Table */}
            <div id="user-dashboard-table">
              <UserDashboardTable
                departments={data.departments}
                initialDepartmentId={departmentFilter}
                onDepartmentFilterChange={setDepartmentFilter}
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const DashboardSkeleton: React.FC = () => (
  <div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, marginBottom: 32 }}>
      {Array.from({ length: 2 }).map((_, i) => (
        <div key={i} style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 24 }}>
          <Skeleton variant="text" width="40%" height={14} />
          <Skeleton variant="text" width="35%" height={36} style={{ marginTop: 20 }} />
          <Skeleton variant="rect" height={6} style={{ marginTop: 20, borderRadius: 3 }} />
        </div>
      ))}
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, marginBottom: 32 }}>
      {Array.from({ length: 2 }).map((_, i) => (
        <div key={i} style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 24 }}>
          <Skeleton variant="text" width="30%" height={14} />
          <Skeleton variant="rect" height={140} style={{ marginTop: 20, borderRadius: 6 }} />
        </div>
      ))}
    </div>
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 24 }}>
      <Skeleton variant="text" width="15%" height={14} />
      <Skeleton variant="rect" height={260} style={{ marginTop: 20, borderRadius: 6 }} />
    </div>
  </div>
);
